import { useMemo } from 'react';
import { OFFERING_CATEGORY_OPTIONS, STATUS_OPTIONS } from '../constants';

export const useOfferingsFormFields = () => {
    return useMemo(
        () => [
            // Row 1: Name (Left) | Category (Right)
            {
                name: 'name',
                label: 'Name',
                type: 'text' as const,
                required: true,
                placeholder: 'Enter offering name',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    maxLength: 100,
                },
                tooltip: 'Enter the name of the offering/service',
            },
            {
                name: 'category',
                label: 'Category',
                type: 'select' as const,
                required: true,
                placeholder: 'Select category',
                layout: { span: 12 },
                options: OFFERING_CATEGORY_OPTIONS,
                props: {
                    size: 'large',
                    showSearch: true,
                    optionFilterProp: 'children',
                },
                tooltip: 'Select the category this offering belongs to',
            },
            // Row 2: Search Term | Price
            {
                name: 'search_term',
                label: 'Search Terms',
                type: 'searchTermTag' as const,
                required: false,
                placeholder: 'Add search terms...',
                layout: { span: 12 },
                maxTags: 10,
                props: {
                    size: 'large',
                },
                tooltip: 'Add search terms to help filter offerings',
            },
            {
                name: 'price',
                label: 'Price',
                type: 'number' as const,
                required: false,
                placeholder: 'Enter price',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    step: 0.01,
                    precision: 2,
                    prefix: '₹',
                },
                tooltip: 'Enter the price of the offering',
            },

            // Row 3: GST Rate | Duration
            {
                name: 'gst_rate',
                label: 'GST Rate (%)',
                type: 'number' as const,
                required: false,
                placeholder: 'Enter GST rate',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    min: 0,
                    max: 100,
                    step: 0.01,
                    precision: 2,
                    suffix: '%',
                },
                tooltip: 'Enter the GST rate percentage (if applicable)',
            },

            {
                name: 'duration_in_mins',
                label: 'Duration (minutes)',
                type: 'number' as const,
                required: false,
                placeholder: 'Enter duration',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    min: 0,
                    step: 1,
                    suffix: 'mins',
                },
                tooltip: 'Enter the duration of the service in minutes',
            },

            // Row 4: Total Sessions | GST Inclusive
            {
                name: 'total_sessions',
                label: 'Total Sessions',
                type: 'number' as const,
                required: false,
                placeholder: 'Enter total sessions',
                defaultValue: 1,
                layout: { span: 12 },
                props: {
                    size: 'large',
                    min: 0.5,
                    step: 0.5,
                    precision: 2,
                },
                tooltip: 'Enter the number of sessions included',
            },

            {
                name: 'is_gst_inclusive',
                label: 'GST Inclusive',
                type: 'switch' as const,
                required: false,
                defaultValue: false,
                layout: { span: 6 },
                props: {
                    size: 'default',
                    checkedChildren: 'Inclusive',
                    unCheckedChildren: 'Exclusive',
                },
                tooltip: 'Toggle whether GST is included in the price',
            },
            {
                name: 'is_active',
                label: 'Status',
                type: 'switch' as const,
                required: false,
                defaultValue: true,
                layout: { span: 6 },
                props: {
                    size: 'default',
                    checkedChildren: 'Active',
                    unCheckedChildren: 'Inactive',
                },
                tooltip: 'Toggle whether this offering is active',
            },

            // Row 5: Discount
            {
                name: 'discount',
                label: 'Discount',
                type: 'number' as const,
                required: false,
                placeholder: 'Enter discount',
                layout: { span: 12 },
                props: {
                    size: 'large',
                    min: 0,
                    max: 100,
                    step: 0.01,
                    precision: 2,
                    suffix: '%',
                },
                tooltip: 'Enter the discount percentage for the offering',
            },
            // Row 5: Description
            {
                name: 'description',
                label: 'Description',
                type: 'textarea' as const,
                required: true,
                placeholder: 'Enter offering description...',
                layout: { span: 24 },
                props: {
                    rows: 4,
                    showCount: true,
                    maxLength: 100,
                },
                tooltip: 'Enter a brief description of the offering',
            },
        ],
        []
    );
}; 